'use client';

import {
  captureSensorsMirrorFacts,
  createSensorsMirrorEventToken,
  generateUuidV4,
} from '@nextmar/snowplow-sdk';
import type {
  CapturedEventInput,
  PreparedSensorsMirror,
  SensorsMirrorEventToken,
  SensorsMirrorFacts,
  SnowplowClient,
} from '@nextmar/snowplow-sdk';
import type {
  ManualAnalyticsCoordinator,
  ManualAnalyticsCounters,
  ManualAnalyticsDependencies,
  ManualAnalyticsDiagnostic,
  ManualIntent,
  ManualIntentRegistration,
  ManualOperation,
  SensorsEnvelope,
  SensorsRuntime,
  SensorsStageContext,
} from './manualAnalyticsTypes';
import {
  bindSensorsMirrorHookTarget,
  installSensorsMirrorHooks,
  MANUAL_OCCURRENCE_PROPERTY,
} from './sensorsMirrorHooks';

/** 仅等待初始化和纯准备，不为传输失败重放 SDK 调用。 */
const READINESS_RETRY_MS = 500;
/**
 * 检查原始身份控制字符，不通过 trim 把非法值变成有效身份。
 * @param value 原始字符串，不包含身份兜底逻辑。
 * @returns 含 C0、DEL 或 C1 时为 true，空字符串不属于控制字符。
 */
export function hasManualIdentityControlCharacters(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || (code >= 127 && code <= 159);
  });
}
/** 一条父意图保留执行游标，已经调用的业务 API 永不倒退。 */
interface IntentRecord {
  token: string;
  intent: ManualIntent;
  cursor: number;
  events: EventRecord[];
  status: 'waiting' | 'executing';
  failed: boolean;
  /** 仅私有保留不可变失败原因，不依赖外部观察者。 */
  failures: Readonly<ManualAnalyticsDiagnostic>[];
}
/** 同步 API 执行范围，track 用私有标记，login 仅接纳实际 signup。 */
interface ActiveOperation {
  intent: IntentRecord;
  operation: ManualOperation;
  marker: string;
}
/** 每条真实事件独立令牌和两个交接状态，重复关联 ID 不去重。 */
interface EventRecord {
  parent: IntentRecord;
  operation: Extract<ManualOperation, { kind: 'track' | 'login' }>;
  token: SensorsMirrorEventToken;
  data: any;
  correlationId: string;
  envelope?: SensorsEnvelope;
  resume?: () => void;
  prepared?: PreparedSensorsMirror;
  state: 'pending' | 'completed' | 'failed';
  mirrorSubmitted: boolean;
  /** 已冻结的发送事实随失败记录保留。 */
  facts?: SensorsMirrorFacts;
  /** 已开始的 Classic 调用结果不明，仍继续 Sensors，但不能计成功。 */
  mirrorUncertainReason?: string;
}
/** 配置明确关闭渠道时，保留既有开关，不伪造双路完成。 */
export interface ManualAnalyticsPolicy {
  sensorsEnabled: boolean;
  mirrorEnabled: boolean;
}
/** 仅 Bridge 持有的配置控制，不扩展业务协调器接口或创建队列副本。 */
const policyControls = new WeakMap<
  ManualAnalyticsCoordinator,
  (policy: ManualAnalyticsPolicy) => void
>();

/** @param owner 本模块工厂拥有者。@param policy 原解析开关。@returns 无返回值，不修改 SDK 全局配置。 */
export function configureManualAnalyticsPolicy(
  owner: ManualAnalyticsCoordinator,
  policy: ManualAnalyticsPolicy,
): void {
  policyControls.get(owner)?.(policy);
}

/**
 * 检查同实例实时身份，不 trim 最终事实，不读取缓存或业务 UUID。
 * @param value 当前 SDK getter 返回值。
 * @returns 是否可以作为执行前门禁。
 */
function validIdentity(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    !hasManualIdentityControlCharacters(value) &&
    !!value.trim() &&
    !['null', 'undefined', 'false', 'sdk is not initialized.'].includes(value.trim().toLowerCase())
  );
}

/**
 * 复制业务 JSON，保留 undefined 空快照，不保留调用方可写引用。
 * @param value 业务属性，不包含 SDK opaque 句柄或业务回调。
 * @returns 独立数据；循环或异常 getter 抛错，登记明确失败。
 */
function copyProperties(value: Readonly<Record<string, unknown>> = {}): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    const item = value[key];
    // defineProperty 保留 __proto__ 等自有键，不触发旧对象 setter，也不依赖新 API。
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      writable: true,
      value: item === undefined ? undefined : JSON.parse(JSON.stringify(item)),
    });
  }
  return result;
}

/**
 * 按原 SP 映射的结构解释 Sensors 的最终值。
 * @param mapped 发生时已映射字段，用于区分对象协议与本来就是字符串的字段。
 * @param actual 神策规范化及插件处理后的最终值。
 * @returns 对象/数组映射恢复其 JSON 结构，其余字段保留最终标量；不改写原 Sensors 数据。
 */
function mappedFinalValue(mapped: unknown, actual: unknown): unknown {
  if (mapped && typeof mapped === 'object' && typeof actual === 'string') {
    try {
      const parsed = JSON.parse(actual);
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed) === Array.isArray(mapped))
        return parsed;
    } catch {
      /* 非 JSON 字符串可能来自明确的属性插件，保持其最终值。 */
    }
  }
  return actual;
}

/**
 * 创建一个当前文档的手动事件 FIFO。
 * @param deps 初始化唤醒、页内令牌及纯标量诊断。
 * @returns 仅当前文档持有的协调器，不读取/写入恢复存储。
 */
export function createManualAnalyticsCoordinator(
  deps: ManualAnalyticsDependencies,
): ManualAnalyticsCoordinator {
  /** 父意图的唯一队列；不设容量拒绝或过期淘汰。 */
  const queue: IntentRecord[] = [];
  /** 当前文档失败证据的唯一强引用，不重放、不落盘、不淘汰；dispose 时释放。 */
  const failedRecords = new Set<IntentRecord>();
  /** 原事件对象映射到真实事件记录，不按关联 ID 做去重。 */
  let events = new WeakMap<object, EventRecord>();
  /** 只有正在执行的本站 track 才持有有效来源标记。 */
  const markers = new Map<string, ActiveOperation>();
  /** 守恒计数始终累计，日志容量不影响这些值。 */
  const counters: ManualAnalyticsCounters = {
    registeredIntents: 0,
    registrationFailures: 0,
    waitingIntents: 0,
    executingIntents: 0,
    completedIntents: 0,
    failedIntents: 0,
    assembledEvents: 0,
    pendingEvents: 0,
    completedEvents: 0,
    failedEvents: 0,
    createdMirrors: 0,
    sensorsSubmissions: 0,
    classicSubmissions: 0,
    noEventIntents: 0,
  };
  let sensors: SensorsRuntime | undefined;
  let client: SnowplowClient | undefined;
  let active: ActiveOperation | undefined;
  let running = false;
  let wakeAgain = false;
  let disposed = false;
  let sensorsEnabled = true;
  let mirrorEnabled = true;
  let sequence = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /** @param record 父意图。@param stage 稳定阶段。@param event 真实事件。@param reason 稳定错误。@returns 无返回值，诊断异常不影响队列。 */
  function diagnostic(
    record: IntentRecord,
    stage: string,
    event?: EventRecord,
    reason?: string,
  ): void {
    const entry = Object.freeze({
      stage,
      source: record.intent.source,
      intentToken: record.token,
      ...(event ? { sensorsEventId: event.correlationId } : {}),
      ...(reason ? { reason } : {}),
    });
    if (reason && !disposed) {
      record.failures.push(entry);
      failedRecords.add(record);
    }
    try {
      deps.onDiagnostic(entry);
    } catch {
      /* 诊断观察者不拥有失败记录或原载荷。 */
    }
  }

  /** @param event 本次真实事件。@param reason 稳定错误。@returns 无返回值，不虚报完成，不重放业务 API。 */
  function failEvent(event: EventRecord, reason: string): void {
    if (event.state !== 'pending') return;
    event.state = 'failed';
    event.parent.failed = true;
    counters.failedEvents += 1;
    counters.pendingEvents -= 1;
    diagnostic(event.parent, 'integration_failed', event, reason);
  }

  /** @param data 原组装事件。@returns 无返回值，只关联本站来源，不冻结原对象。 */
  function associate(data: any): void {
    if (!data || typeof data !== 'object' || events.has(data)) return;
    const marker = data.properties?.[MANUAL_OCCURRENCE_PROPERTY];
    const owner =
      markers.get(marker) ??
      (active?.operation.kind === 'login' && data.type === 'track_signup' ? active : undefined);
    if (data.properties && marker !== undefined) delete data.properties[MANUAL_OCCURRENCE_PROPERTY];
    if (disposed || !owner || (data.type !== 'track' && data.type !== 'track_signup')) return;
    if (owner.operation.kind !== 'track' && owner.operation.kind !== 'login') return;
    const originalId = data.properties?.sensors_event_id;
    const event: EventRecord = {
      parent: owner.intent,
      operation: owner.operation,
      token: createSensorsMirrorEventToken(),
      data,
      correlationId:
        typeof originalId === 'string' && originalId.trim() ? originalId : generateUuidV4(),
      state: 'pending',
      mirrorSubmitted: false,
    };
    events.set(data, event);
    owner.intent.events.push(event);
    counters.assembledEvents += 1;
    counters.pendingEvents += 1;
    data.time = owner.intent.intent.occurredAt;
    const properties = {
      ...data.properties,
      ...copyProperties(owner.intent.intent.sensorsProperties),
      sensors_event_id: event.correlationId,
    };
    const operationProperties =
      owner.operation.kind === 'track' ? owner.operation.properties : undefined;
    const hasOperationSpm =
      operationProperties && Object.prototype.hasOwnProperty.call(operationProperties, 'spm');
    const spm = hasOperationSpm ? operationProperties.spm : properties.spm;
    // 空快照必须遮蔽页面公共来源；最终载荷不发送空 spm 字段。
    if (spm === null || spm === undefined) delete properties.spm;
    else if (hasOperationSpm) properties.spm = spm;
    data.properties = properties;
    delete data.properties[MANUAL_OCCURRENCE_PROPERTY];
  }

  /** @param envelope 官方原对象。@param context 官方 gate 上下文。@param resume 同一发送阶段的继续函数。@returns 是否为本站目标。 */
  function pause(
    envelope: SensorsEnvelope,
    context: SensorsStageContext,
    resume: () => void,
  ): boolean {
    if (!envelope || typeof envelope !== 'object') return false;
    // 组装观察者失败时仍可从同一原对象完成纯关联，不再调用 track/login。
    const original = envelope.origin_data ?? envelope.data;
    if (!original || typeof original !== 'object') return false;
    if (!events.has(original)) associate(original);
    const event = events.get(original);
    if (!event) return false;
    context.cancellationToken.stop();
    if (event.state !== 'pending' || event.envelope) return true;
    event.envelope = envelope;
    event.resume = resume;
    if (context.getPosition().current !== 1) failEvent(event, 'send_interceptor_order');
    return true;
  }

  /** @param data 已组装对象。@returns 无返回值；观察通知不创建或提交镜像。 */
  function observe(data: any): void {
    const event = data && typeof data === 'object' ? events.get(data) : undefined;
    if (event) diagnostic(event.parent, 'event_assembled', event);
  }

  /** @param event 当前真实事件。@returns 保留 SP 派生映射，对最终业务字段应用增删并排除神策保留属性。 */
  function finalInput(event: EventRecord): CapturedEventInput {
    const input = event.operation.mirrorInput;
    if (input.kind === 'signUp') return input;
    const mapped = (input.properties as Record<string, unknown>) ?? {};
    const properties = copyProperties(mapped);
    const original = event.operation.kind === 'track' ? event.operation.properties || {} : {};
    const snapshot = event.parent.intent.sensorsProperties || {};
    const final = event.envelope!.data.properties || {};
    // 清除来自 Sensors 的旧字段；只留原映射中独立于 Sensors 的 SP 派生字段。
    for (const key of Object.keys(properties)) {
      if (
        Object.prototype.hasOwnProperty.call(original, key) ||
        Object.prototype.hasOwnProperty.call(snapshot, key)
      )
        delete properties[key];
    }
    for (const key of Object.keys(final)) {
      // 神策保留属性不整体复制；两身份只能由可信事实写入公共 context。
      if (
        key.startsWith('$') ||
        key === 'distinct_id' ||
        key === 'anonymous_id' ||
        key === MANUAL_OCCURRENCE_PROPERTY
      )
        continue;
      if (
        Object.prototype.hasOwnProperty.call(mapped, key) ||
        !Object.prototype.hasOwnProperty.call(original, key) ||
        key.startsWith('pub_')
      ) {
        properties[key] = mappedFinalValue(mapped[key], final[key]);
      }
    }
    return { ...input, properties } as CapturedEventInput;
  }

  /** @param event 已执行 API 的真实事件。@returns true 表示有终态，false 仅表示纯准备尚未就绪。 */
  async function deliver(event: EventRecord): Promise<boolean> {
    if (event.state !== 'pending') return true;
    if (!event.envelope || !event.resume || !client) {
      failEvent(event, 'missing_send_envelope');
      return true;
    }
    if (!mirrorEnabled) {
      // 显式 opt-out 沿用原 Sensors 单路；失败计数保留 disabled，绝不计为成功镜像。
      try {
        event.resume();
        counters.sensorsSubmissions += 1;
      } catch {
        failEvent(event, 'sensors_submit_uncertain');
        return true;
      }
      failEvent(event, 'disabled');
      return true;
    }
    const data = event.envelope.data;
    if (!data?.properties || typeof data !== 'object') {
      failEvent(event, 'invalid_final_payload');
      return true;
    }
    // 后置插件删除关联 ID 时恢复本事件已经生成的一项，不生成第二个 ID。
    if (
      typeof data.properties.sensors_event_id !== 'string' ||
      !data.properties.sensors_event_id.trim()
    )
      data.properties.sensors_event_id = event.correlationId;
    const captured = captureSensorsMirrorFacts(event.token, data);
    if (captured.captured === false) {
      failEvent(event, captured.reason);
      return true;
    }
    event.facts = captured.facts;
    event.correlationId = captured.facts.sensorsEventId;
    diagnostic(event.parent, 'event_finalized', event);
    if (!event.prepared) {
      const result = await client.prepareSensorsMirror(
        captured.facts,
        finalInput(event),
        event.parent.intent.context,
      );
      if (result.prepared === false) {
        if (result.reason === 'not_ready') return false;
        failEvent(event, result.reason);
        return true;
      }
      if (disposed) return true;
      event.prepared = result.event;
      counters.createdMirrors += 1;
      diagnostic(event.parent, 'mirror_prepared', event);
    }
    // 等待纯准备时原 SDK 对象仍可变，重新校验同一令牌的完整事实。
    const checked = captureSensorsMirrorFacts(event.token, event.envelope.data);
    if (checked.captured === false) {
      failEvent(event, checked.reason);
      return true;
    }
    if (!event.mirrorSubmitted) {
      const result = await client.submitPreparedSensorsMirror(event.prepared);
      if (disposed) return true;
      if (result.status === 'pending') return false;
      if (result.status !== 'handed-off' && result.status !== 'uncertain') {
        failEvent(event, result.reason);
        return true;
      }
      if (result.status === 'uncertain') event.mirrorUncertainReason = result.reason;
      event.mirrorSubmitted = true;
      counters.classicSubmissions += 1;
      diagnostic(event.parent, 'mirror_submitted', event);
    }
    if (disposed) return true;
    const beforeResume = captureSensorsMirrorFacts(event.token, event.envelope.data);
    if (beforeResume.captured === false) {
      failEvent(event, beforeResume.reason);
      return true;
    }
    try {
      event.resume();
      counters.sensorsSubmissions += 1;
      diagnostic(event.parent, 'sensors_submitted', event);
    } catch {
      failEvent(event, 'sensors_submit_uncertain');
      return true;
    }
    // Classic 已开始的不明确结果不阻断 Sensors，也不重放任何一路或虚报成功。
    if (event.mirrorUncertainReason) {
      failEvent(event, event.mirrorUncertainReason);
      return true;
    }
    event.state = 'completed';
    counters.completedEvents += 1;
    counters.pendingEvents -= 1;
    return true;
  }

  /** @param record 当前父意图。@param operation 下一项业务 API。@returns 无返回值，业务 API 最多执行一次。 */
  function execute(record: IntentRecord, operation: ManualOperation): void {
    const scope: ActiveOperation = {
      intent: record,
      operation,
      marker: `${record.token}:${record.cursor}`,
    };
    const before = record.events.length;
    active = scope;
    markers.set(scope.marker, scope);
    try {
      if (operation.kind === 'track')
        sensors!.track(operation.eventName, {
          ...copyProperties(operation.properties),
          [MANUAL_OCCURRENCE_PROPERTY]: scope.marker,
        });
      else if (operation.kind === 'login') {
        const previous = sensors!.store?.getDistinctId?.();
        sensors!.login(operation.uuid);
        operation.afterIdentity?.();
        if (record.events.length === before && previous !== operation.uuid) {
          record.failed = true;
          diagnostic(record, 'integration_failed', undefined, 'missing_login_event');
        }
      } else if (operation.kind === 'logout') sensors!.logout(operation.reset);
      else sensors!.setProfile(copyProperties(operation.properties));
      if (operation.kind === 'track' && record.events.length === before) {
        record.failed = true;
        diagnostic(record, 'integration_failed', undefined, 'missing_track_event');
      }
    } catch {
      record.failed = true;
      diagnostic(record, 'integration_failed', undefined, 'business_api_failed');
    } finally {
      active = undefined;
      markers.delete(scope.marker);
    }
  }

  /** 无入参；等待 gate 完整后推进队首，返回异步完成，不修改已执行游标。 */
  async function pump(): Promise<void> {
    if (!queue.length || disposed) return;
    let ready = false;
    try {
      ready = await deps.ensureReady();
    } catch {
      /* 明确等待，可在后续 wake 重试初始化。 */
    }
    if (disposed || !ready || !sensors || !client) return;
    while (queue.length && !disposed) {
      if (!isExecutionReady()) return;
      const record = queue[0];
      if (record.status === 'waiting') {
        record.status = 'executing';
        counters.waitingIntents -= 1;
        counters.executingIntents += 1;
        diagnostic(record, 'intent_executing');
      }
      while (!disposed) {
        for (const event of record.events) {
          try {
            if (!(await deliver(event))) return;
          } catch {
            failEvent(event, 'mirror_integration_failed');
          }
        }
        if (disposed || record.cursor >= record.intent.operations.length) break;
        if (!isExecutionReady()) return;
        const operation = record.intent.operations[record.cursor++];
        execute(record, operation);
      }
      if (disposed) return;
      queue.shift();
      counters.executingIntents -= 1;
      if (record.failed) counters.failedIntents += 1;
      else {
        counters.completedIntents += 1;
        if (!record.events.length) {
          counters.noEventIntents += 1;
          diagnostic(record, 'no_event');
        }
      }
    }
  }

  /** 无入参；每个待执行动作之前重新检查同实例完整身份，返回是否可执行，不改写已冻结事件。 */
  function isExecutionReady(): boolean {
    try {
      return (
        sensorsEnabled &&
        !!client &&
        !!sensors &&
        (!mirrorEnabled || client.getState().status === 'ready') &&
        validIdentity(sensors.store?.getDistinctId?.()) &&
        validIdentity(sensors.quick('getAnonymousID'))
      );
    } catch {
      return false;
    }
  }

  /** 无入参；并发 wake 合并为单个 pump，返回 void，不绕过 FIFO。 */
  function wake(): void {
    if (disposed || !queue.length) return;
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (running) {
      wakeAgain = true;
      return;
    }
    running = true;
    void Promise.resolve()
      .then(pump)
      .finally(() => {
        running = false;
        if (disposed || !queue.length) return;
        if (wakeAgain) {
          wakeAgain = false;
          wake();
        } else
          timer = setTimeout(() => {
            timer = undefined;
            wake();
          }, READINESS_RETRY_MS);
      });
  }

  /** @param input 已捕获业务意图。@returns 仅登记结果；实际资源异常可观察且不虚报 accepted。 */
  function register(input: ManualIntent): ManualIntentRegistration {
    if (disposed) return { accepted: false, reason: 'disposed' };
    if (!sensorsEnabled) return { accepted: false, reason: 'disabled' };
    try {
      if (
        !input ||
        typeof input !== 'object' ||
        typeof input.source !== 'string' ||
        !/^[a-zA-Z0-9._:-]{1,128}$/.test(input.source) ||
        !Number.isFinite(input.occurredAt) ||
        !Array.isArray(input.operations) ||
        !input.operations.length ||
        !input.context ||
        input.operations.some(
          (operation) =>
            !operation || !['track', 'login', 'logout', 'profile'].includes(operation.kind),
        )
      ) {
        counters.registrationFailures += 1;
        return { accepted: false, reason: 'invalid_intent' };
      }
      const intent: ManualIntent = {
        ...input,
        sensorsProperties: copyProperties(input.sensorsProperties),
        operations: input.operations.map((operation) => ({
          ...operation,
          ...('properties' in operation
            ? { properties: copyProperties(operation.properties) }
            : {}),
          ...('mirrorInput' in operation
            ? {
                mirrorInput: JSON.parse(
                  JSON.stringify(operation.mirrorInput),
                ) as CapturedEventInput,
              }
            : {}),
        })),
      };
      const record: IntentRecord = {
        token: `manual-${++sequence}-${deps.createIntentId()}`,
        intent,
        cursor: 0,
        events: [],
        status: 'waiting',
        failed: false,
        failures: [],
      };
      queue.push(record);
      counters.registeredIntents += 1;
      counters.waitingIntents += 1;
      diagnostic(record, 'intent_registered');
      diagnostic(record, 'intent_waiting');
      wake();
      return { accepted: true, intentToken: record.token };
    } catch {
      counters.registrationFailures += 1;
      return { accepted: false, reason: 'registration_failed' };
    }
  }

  /** @param value 当前窗口实际 SDK。@returns 无返回值；安装失败不执行队列，保留等待记录。 */
  function install(value: SensorsRuntime): void {
    if (disposed || sensors === value) return;
    installSensorsMirrorHooks(value, coordinator);
    sensors = value;
    wake();
  }
  /** @param value 同一文档的唯一 Browser Client。@returns 无返回值，仅唤醒现有等待。 */
  function connect(value: SnowplowClient): void {
    if (!disposed && client !== value) {
      client = value;
      wake();
    }
  }
  /** 无入参；返回计数副本，调用者不能修改内部累计值。 */
  function getCounters(): Readonly<ManualAnalyticsCounters> {
    return Object.freeze({ ...counters });
  }
  /** 无入参；释放当前文档等待，明确失败，不把未提交记录算完成。 */
  function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = undefined;
    for (const record of queue) {
      for (const event of record.events) failEvent(event, 'disposed');
      counters.failedIntents += 1;
    }
    queue.length = 0;
    counters.waitingIntents = 0;
    counters.executingIntents = 0;
    markers.clear();
    failedRecords.clear();
    // 第三方可能继续持有原对象；替换关联表，释放本站 event/parent/失败原因引用。
    events = new WeakMap<object, EventRecord>();
    sensors = undefined;
    client = undefined;
  }
  const coordinator: ManualAnalyticsCoordinator = {
    register,
    install,
    connect,
    wake,
    getCounters,
    dispose,
  };
  policyControls.set(coordinator, (policy) => {
    sensorsEnabled = policy.sensorsEnabled;
    mirrorEnabled = policy.mirrorEnabled;
    if (!sensorsEnabled) {
      if (timer) clearTimeout(timer);
      timer = undefined;
      for (const record of queue) {
        for (const event of record.events) failEvent(event, 'disabled');
        counters.failedIntents += 1;
        diagnostic(record, 'integration_failed', undefined, 'disabled');
      }
      queue.length = 0;
      counters.waitingIntents = 0;
      counters.executingIntents = 0;
    } else wake();
  });
  bindSensorsMirrorHookTarget(coordinator, { associate, observe, pause });
  return coordinator;
}
